import React from 'react';
import { useState, useEffect } from 'react';
import firebase from '../../config/firebaseConfig';
import { db } from '../../config/firebaseConfig';
import Calendar from 'react-calendar';
import { Button, Card, Container, Form, Modal, Toast, ToastContainer, OverlayTrigger, Tooltip, Spinner } from 'react-bootstrap';
import GoogleSyncButton from './GoogleSyncButton';
import 'react-calendar/dist/Calendar.css';
import { SIGNS } from '../../config/astrologie';
import SidebarDashboard from './SidebarDashboard';
import moment from 'moment';
import 'moment/locale/fr';
import '@animxyz/core';
import { XyzTransition } from '@animxyz/react';

function CalendarPage() {
  const [tasks, setTasks] = useState([]); // État local pour stocker les tâches en direct
  // On initialise le calendrier directement sur l'année en cours (2026)
  const [loading, setLoading] = useState(true);
  const [value, onChange] = useState(new Date()); // 2026, 8, 9 => 09 Septembre 2026


  const [birthdays, setBirthdays] = useState([]);
  const [showBirthdayModal, setShowBirthdayModal] = useState(false);
  const [birthdayForm, setBirthdayForm] = useState({ name: '', date: '' });

  const [deaths, setDeaths] = useState([]);
  const [showDeathModal, setShowDeathModal] = useState(false);
  const [deathForm, setDeathForm] = useState({ name: '', birthDate: '', deathDate: '' });

  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState('success');

  const triggerToast = (message, variant = 'success') => {
    setToastMessage(message);
    setToastVariant(variant);
    setShowToast(true);
  };

  // 🔄 SYNCHRONISATION EN DIRECT AVEC FIREBASE FIRESTORE
  useEffect(() => {
    const unsubscribeAuth = firebase.auth().onAuthStateChanged((firebaseUser) => {
      if (firebaseUser && firebaseUser.uid) {

        // 🚀 On écoute la collection 'birthdays' filtrée par l'utilisateur connecté
        const unsubscribeFirestore = db.collection('birthdays')
          .where('authorId', '==', firebaseUser.uid)
          .onSnapshot((querySnapshot) => {
            const list = [];
            querySnapshot.forEach((doc) => {
              list.push({ id: doc.id, ...doc.data() }); // doc.id devient l'identifiant unique
            });
            setBirthdays(list);
          }, (error) => {
            console.error("Erreur Firestore :", error);
          });

        // 🚀 2. NOUVEL ÉCOUTEUR : En temps réel pour les décès
        const unsubscribeDeaths = db.collection('deaths')
          .where('authorId', '==', firebaseUser.uid)
          .onSnapshot((querySnapshot) => {
            const list = [];
            querySnapshot.forEach((doc) => { list.push({ id: doc.id, ...doc.data() }); });
            setDeaths(list);
          });

        return () => {
          unsubscribeFirestore();
          unsubscribeDeaths();
        }
      } else {
        setBirthdays([]); // Vide l'écran si déconnexion
        setDeaths([]); // Réinitialisation au logout
      }
    });

    return () => unsubscribeAuth();
  }, []);

  const [holidays, setHolidays] = useState({});
  const currentYear = new Date().getFullYear();

  useEffect(() => {
    // Récupère les jours fériés officiels pour la métropole
    fetch('https://calendrier.api.gouv.fr/jours-feries/metropole.json')
      .then(response => response.json())
      .then(data => setHolidays(data))
      .catch(error => console.error("Erreur jours fériés:", error));
  }, []);

  // 👑 RECONNEXION : Écoute de la collection racine 'tasks' globale
  useEffect(() => {
    let unsubscribeFirestore = () => { };

    // On attend que Firebase valide l'état de l'utilisateur
    const unsubscribeAuth = firebase.auth().onAuthStateChanged((user) => {

      if (user) {
        // L'utilisateur est connecté, on peut brancher Firestore de manière sécurisée
        unsubscribeFirestore = db.collection('tasks')
          .where('authorId', '==', user.uid)
          .onSnapshot(
          (snapshot) => {
            const tasksList = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            setTasks(tasksList);
            setLoading(false);
          },
          (error) => {
            console.error("Erreur de chargement Firestore :", error);
            setLoading(false);
          }
          );
      } else {
        console.warn("Aucun utilisateur connecté, impossible de charger les rendez-vous.");
        setTasks([]); // On vide les tâches si déconnecté
      }
    });

    // Nettoyage propre des deux écouteurs au démontage du composant
    return () => {
      unsubscribeAuth();
      unsubscribeFirestore();
    };
  }, []);

  const getZodiacSign = (dateString) => {
    if (!dateString) return { name: '', emoji: '' };

    const date = new Date(dateString);
    const month = date.getMonth() + 1; // Les mois vont de 0 à 11 en JS
    const day = date.getDate();

    const found = SIGNS.find(s => {
      if (s.start.m === month && day >= s.start.d) return true;
      if (s.end.m === month && day <= s.end.d) return true;
      return false;
    });

    // Gestion du cas de transition de fin d'année pour le Capricorne
    if (!found) {
      return { name: 'Capricorne', emoji: '♑' };
    }

    return { name: found.name, emoji: found.emoji };
  };

  const sortBirthdaysByDate = (list) => {
    return [...list].sort((a, b) => {
      const dateA = new Date(a.date);
      const dateB = new Date(b.date);

      // 1. On compare d'abord les mois (0 = Janvier, 11 = Décembre)
      if (dateA.getMonth() !== dateB.getMonth()) {
        return dateA.getMonth() - dateB.getMonth();
      }
      // 2. Si les mois sont identiques, on compare les jours (1 à 31)
      return dateA.getDate() - dateB.getDate();
    });
  };

  const calculateAgeAtDeath = (birthDateString, deathDateString) => {
    if (!birthDateString || !deathDateString) return null;
    const birth = moment(birthDateString);
    const death = moment(deathDateString);

    let age = death.year() - birth.year();
    const monthDiff = death.month() - birth.month();

    if (monthDiff < 0 || (monthDiff === 0 && death.date() < birth.date())) {
      age--;
    }
    return age > 0 ? age : null;
  };

  const calculateAge = (dateString, referenceDate = new Date()) => {
    if (!dateString) return null;

    const birthDate = moment(dateString);
    const refMoment = moment(referenceDate);

    const birthYear = birthDate.year();
    const refYear = refMoment.year();

    // Si l'année de naissance est l'année en cours (pas d'année passée réelle saisie)
    if (birthYear === moment().year()) {
      return null;
    }

    // Calcul de l'âge par rapport à l'année de la case affichée
    let age = refYear - birthYear;
    const monthDiff = refMoment.month() - birthDate.month();

    if (monthDiff < 0 || (monthDiff === 0 && refMoment.date() < birthDate.date())) {
      age--;
    }

    return age > 0 ? age : null;
  };

  // Fonction pour mettre la première lettre en majuscule
  const capitaliserMois = (locale, date) => {
    // Récupère le format natif ex: "septembre 2026"
    const labelIdoine = new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric' }).format(date);
    // Retourne la chaîne avec la première lettre en majuscule
    return labelIdoine.charAt(0).toUpperCase() + labelIdoine.slice(1);
  };

  // 👑 RECOVERY : Extraction et conversion dynamique des rendez-vous
  const getRdvDates = () => {
    console.log("Tâches reçues de Redux/State :", tasks);
    if (!tasks || tasks.length === 0) return {};

    // Tableaux de correspondance pour décoder manuellement si moment bloque
    // const moisFr = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

    const map = {};

    tasks
      .filter(task => task.rdvDate && task.rdvDate.trim() !== '' && task.rdvDate !== 'Aucun')
      .forEach(task => {
        try {
          let formattedDate = null;

          // 🎯 1. Nouvelle sécurité : Détection du format ISO direct (contient un 'T')
          if (task.rdvDate.includes && task.rdvDate.includes('T')) {
            formattedDate = task.rdvDate.split('T')[0]; // Extrait "2026-09-15"
          } else if (task.rdvDate.includes && task.rdvDate.includes(' ')) {
            // Si c'est du texte français "samedi 26 septembre 2026"
            formattedDate = moment(task.rdvDate, 'dddd D MMMM YYYY', 'fr').format('YYYY-MM-DD');
          } else {
            formattedDate = moment(task.rdvDate).format('YYYY-MM-DD'); // Format propre YYYY-MM-DD
          }

          // Si la date est valide, on groupe les tâches par jour
          if (formattedDate && moment(formattedDate, 'YYYY-MM-DD', true).isValid()) {
            if (!map[formattedDate]) {
              map[formattedDate] = [];
            }
            map[formattedDate].push(task);
          }
        } catch (err) {
          console.error("Erreur décodage date :", err);
        }
      });

    return map;
  };

  // Tableau dynamique contenant toutes les dates de rendez-vous
  const rdvMap = getRdvDates();
  console.log("Dates de rendez-vous formatées pour le calendrier :", rdvMap);

  // Fonction pour ajouter une puce visuelle sur les jours contenant un RDV
  const tileContent = ({ date, view }) => {
    const elementsAAfficher = [];

    if (view === 'month') {
      const formattedDate = moment(date).format('YYYY-MM-DD');
      const currentTileMMDD = moment(date).format('DD-MM');

      // 1. --- LOGIQUE DES ANNIVERSAIRES ---
      const birthdayFound = birthdays.find(b => moment(b.date).format('DD-MM') === currentTileMMDD);

      if (birthdayFound) {
        const ageFete = calculateAge(birthdayFound.date, date);  // 🚀 On passe la variable 'date' locale de la case
        const titleText = ageFete
          ? `Anniversaire de ${birthdayFound.name} (${ageFete} ans) !`
          : `Anniversaire de ${birthdayFound.name} !`;

        elementsAAfficher.push(
          <div
            key={`birthday-${birthdayFound.id}`}
            style={{ fontSize: '14px', marginTop: '2px', cursor: 'pointer' }}
            title={titleText}
            onClick={(e) => {
              e.stopPropagation(); // Évite de déclencher le changement de jour du calendrier
              alert(titleText);    // 🚀 Affiche une jolie notification système native au doigt !
            }}
          >
            🎁
          </div>
        );
      }

      // 🚀 NOUVEAU : LOGIQUE DES COMMÉMORATIONS DANS LE CALENDRIER
      const deathFound = deaths.find(d => moment(d.deathDate).format('DD-MM') === currentTileMMDD);
      if (deathFound) {
        // Calcul de l'âge qu'avait la personne à son décès
        const ageAtDeath = calculateAgeAtDeath(deathFound.birthDate, deathFound.deathDate);
        const titleText = ageAtDeath
          ? `Commémoration de ${deathFound.name} (${ageAtDeath} ans à son départ)`
          : `Commémoration de ${deathFound.name}`;

        elementsAAfficher.push(
          <div
            key={`death-${deathFound.id}`}
            style={{ fontSize: '14px', marginTop: '4px', color: '#4a5568', cursor: 'pointer' }}
            title={titleText}
            onClick={(e) => {
              e.stopPropagation(); // Bloque la case du calendrier
              alert(titleText);    // 🚀 Alerte tactile instantanée !
            }}
          >
            🕯️
          </div>
        );
      }

      // 2. --- LOGIQUE DES JOURS FÉRIÉS (Nouveau) ---
      const holidayName = holidays ? holidays[formattedDate] : null;

      if (holidayName) {
        elementsAAfficher.push(
          <OverlayTrigger
            key={`holiday-${formattedDate}`}
            placement="top"
            delay={{ show: 150, hide: 150 }}
            overlay={
              <Tooltip id={`holiday-${formattedDate}`}>
                <div className="fw-semibold text-wrap">{holidayName}</div>
              </Tooltip>
            }
          >
            <div
              style={{
                fontSize: '13px',
                fontWeight: 'bold',
                color: '#dc3545',
                backgroundColor: '#ffe8e8',
                borderRadius: '4px',
                padding: '2px 4px',
                marginTop: '1px',
                cursor: 'help'
              }}
            >
              Férié
            </div>
          </OverlayTrigger>
        );
      }

      // 3. --- LOGIQUE DES RENDEZ-VOUS / TÂCHES ---
      const dayTasks = rdvMap[formattedDate];

      if (dayTasks && dayTasks.length > 0) {
        // 🎯 LOGIQUE DES PICOTS : On regarde si toutes les tâches de la journée sont terminées
        const totalTasks = dayTasks.length;
        const completedTasks = dayTasks.filter(t => t.checked).length;
        const isAllDone = completedTasks === totalTasks;

        // let emojiPicot = "📌"; // 🔴 Couleur par défaut : Épinglé actif

        // Choix du picot selon l'état d'avancement
        const emojiPicot = isAllDone ? "🟢" : "📌";

        // if (completedTasks === totalTasks) {
        //   emojiPicot = "🟢";
        //   // emojiPicot = "✅";
        //   // emojiPicot = "🟩";
        //   // (Si vous préférez le vert, remplacez par "🟢" || "✅" || "🟩à la ligne ci-dessus !)
        // }

        // 👑 CONSTITUTION DE LA BULLE D'INFORMATION (TOOLTIP)
        const renderTooltip = (props) => (
          <Tooltip id={`tooltip-${formattedDate}`} {...props}>
            <div className="text-start p-1" style={{ maxWidth: '220px' }}>
              {dayTasks.map((t, index) => (
                <div key={t.id} className={index > 0 ? "border-top border-secondary mt-1 pt-1" : ""}>
                  <strong className="text-warning">🕒 {t.rdvTime || "Heure non spécifiée"}</strong>
                  <div className="fw-semibold text-white small">{t.task}</div>
                  {t.detail && t.detail !== 'Aucune' && (
                    <div className="fw-semibold text-info text-truncate" style={{ fontSize: '11px' }}>{t.detail}</div>
                  )}
                </div>
              ))}
            </div>
          </Tooltip>
        );

        // On ajoute le picot enveloppé de son Tooltip dans notre liste d'éléments à afficher
        elementsAAfficher.push(
          <OverlayTrigger
            key={`tasks-${formattedDate}`}
            placement="top"
            delay={{ show: 150, hide: 150 }}
            overlay={renderTooltip}
            trigger={['hover', 'focus', 'click']}
            rootClose={true}
          >
            {/* 🎯 RENDU DU PICOT DE COULEUR SÉCURISÉ 1ère solution*/}
            {/* <div className="picot-wrapper">
              <span className={isAllDone ? "has-rdv-picot-done-green" : "has-rdv-picot-red"}></span>
            </div> */}

            {/* 🎯 2ième solution*/}
            {/* <span style={{ fontSize: '0.9rem', marginLeft: '2px' }}>{emojiPicot}</span> */}

            {/* 🚀 PUCE AVEC ANIMATION DE TRANSITION DE COULEUR 3ième solution*/}
            {/* <span
              className={`calendar-custom-picot ${isAllDone ? 'picot-done' : 'picot-pending'}`}
              title={isAllDone ? "Tous les rendez-vous sont bouclés" : "Rendez-vous actif"}
            ></span> */}

            <div style={{ fontSize: '14px', marginTop: '3px', cursor: 'pointer' }}
              onClick={(e) => e.stopPropagation()}
            >
              {emojiPicot}
            </div>

          </OverlayTrigger>
        );
      }
    }
    // 3. --- RENDU DES ÉLÉMENTS ---
    // Si on a un anniversaire, une tâche, ou les deux, on les affiche l'un en dessous de l'autre
    return elementsAAfficher.length > 0 ? (
      <div className="d-flex flex-column align-items-center justify-content-center">
        {elementsAAfficher}
      </div>
    ) : null;
  };

  return (
    <React.Fragment>
      <Container fluid className='min-vh-100 text-light py-4 mt-2 my-0'>
        <div className="d-flex flex-column flex-md-row">
          <SidebarDashboard />
          <div className="admin-main-wrapper w-100">
            <Container className="py-1 bg-profil min-vh-100">

              <Container className="my-4 px-3 px-sm-3 my-sm-3">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-stretch gap-4 mb-5">
                  <XyzTransition appear xyz='fade stagger delay-2 duration-10'>
                    <Container className="d-flex flex-column align-items-start">
                      <h3 className="text-center mx-auto text-warning fw-semibold mb-2 fs-sm-2">📅 Mon Agenda {currentYear}</h3>
                      <Card className="bg-dark text-white mt-2 p-2 shadow-sm border-secondary custom-dashboard-card">


                        {/* 👑 AJOUT : Condition d'affichage (Spinner VS Calendrier) */}
                        {loading ? (
                          <div className="d-flex flex-column justify-content-center align-items-center my-5 py-4">
                            <Spinner animation="border" variant="warning" role="status" className="mb-3">
                              <span className="visually-hidden">Chargement...</span>
                            </Spinner>
                            <div className="text-muted small">Récupération de vos rendez-vous...</div>
                          </div>
                        ) : (
                            <div className="flex-grow-1 w-100" style={{ maxWidth: '100%' }}>
                              <React.Fragment>
                                <Calendar
                                  locale="fr-FR"
                                  key={`${tasks.length}-${birthdays.length}-${deaths.length}`}
                                  onChange={onChange}
                                  value={value}
                                  formatMonthYear={capitaliserMois}
                                  formatShortWeekday={(locale, date) =>
                                    // date.toLocaleDateString(locale, { weekday: 'short' }).charAt(0).toUpperCase()
                                    // date.toLocaleDateString(locale, { weekday: 'long' }).toUpperCase()
                                    date.toLocaleDateString(locale, { weekday: 'short' }).toUpperCase()
                                  }
                                  tileContent={tileContent}
                                  className="custom-dashboard-calendar w-100"
                                  showFixedNumberOfWeeks={true}
                                />
                                <div className="mt-3 text-center fs-6 fs-sm-5">
                                  Jour sélectionné : <span className="text-info fw-semibold">{moment(value).locale('fr').format('DD MMMM YYYY')}</span>
                                </div>
                              </React.Fragment>
                            </div>
                          )}
                      </Card>
                    </Container>
                  </XyzTransition>


                  {/* --- LE BOUTON & LA LISTE --- */}
                  <XyzTransition appear xyz='fade stagger delay-2 duration-10'>
                    <div className="w-100 w-md-auto d-flex flex-column gap-4" style={{ minWidth: '280px', mdWidth: '350px', marginBottom: '10px' }}>
                      {/* --- BLOC DUO DES BOUTONS (Eux restent côte à côte grâce à flex-sm-row) --- */}
                      <div className="d-flex flex-column flex-sm-row gap-3 mt-5 justify-content-center w-100 button-anniv">
                        {/* Bouton Anniversaire existant */}
                        <Button
                          variant="warning"
                          className="text-dark fw-semibold w-100 w-sm-auto "
                          onClick={() => setShowBirthdayModal(true)}
                          style={{ borderRadius: '10px', padding: '12px 20px' }}
                        >
                          <span>🎁</span> Ajouter un anniversaire
                        </Button>

                        {/* 🚀 NOUVEAU BOUTON : Ajouter un décès (Thème sombre respectueux) */}
                        <Button
                          variant="secondary"
                          className="text-white fw-semibold w-100 w-sm-auto"
                          onClick={() => setShowDeathModal(true)}
                          style={{ borderRadius: '10px', padding: '12px 20px', backgroundColor: '#5372a7', borderColor: '#5372a7' }}
                        >
                          <span>🕯️</span> Ajouter un décès
                        </Button>
                      </div>

                      <div className="card shadow-sm border-0 bg-light p-3 rounded-3">
                        <h5 className="card-title fs-6 text-muted mb-3 d-flex align-items-center gap-2">
                          <i className="bi bi-journal-bookmark-fill"></i> Anniversaires ({birthdays.length})
                        </h5>

                        {birthdays.length === 0 ? (
                          <p className="text-muted small mb-0 text-center py-2">Aucun anniversaire enregistré</p>
                        ) : (
                            <ul className="list-unstyled mb-0 d-flex flex-column gap-2" style={{ maxHeight: '240px', overflowY: 'auto' }}>
                              {sortBirthdaysByDate(birthdays).map((b) => {
                                const dateOption = { day: 'numeric', month: 'long', year: 'numeric' };
                                const dateFormatee = new Date(b.date).toLocaleDateString('fr-FR', dateOption);

                                // 🚀 COMPARAISONS SÉCURISÉES AVEC MOMENT :
                                // On compare uniquement le jour et le mois (ex: "30-09"), peu importe l'année de naissance
                                const birthdayMMDD = moment(b.date).format('DD-MM');
                                const todayMMDD = moment().format('DD-MM');
                                const monthMM = moment(b.date).format('MM');
                                const currentMonthMM = moment().format('MM');

                                // 🚀 NOUVEAU CODE BLINDÉ :
                                const currentDay = moment().date(); // Numéro du jour actuel (ex: 2 pour le 2 octobre)
                                const birthdayDay = moment(b.date).date(); // Numéro du jour de l'anniversaire
                                // États dynamiques

                                const isToday = birthdayMMDD === todayMMDD;
                                // const isThisMonth = monthMM === currentMonthMM && !isToday; // Ce mois-ci, mais pas aujourd'hui

                                // C'est ce mois-ci, mais uniquement si le jour n'est pas encore passé !
                                const isThisMonth = monthMM === currentMonthMM && !isToday && birthdayDay > currentDay;

                                // OPTIONNEL : Si tu veux un badge spécial pour dire que c'est passé :
                                const isAlreadyPassedThisMonth = monthMM === currentMonthMM && !isToday && birthdayDay < currentDay;

                                // Choix des styles de fond et bordure
                                let rowClass = "border-warning border-start border-3";
                                let rowStyle = {};

                                if (isToday) {
                                  rowClass = "border-danger shadow-sm border-start border-3";
                                  rowStyle = { backgroundColor: '#ffe8e8' }; // Rouge/Rose clair pour le jour J
                                } else if (isThisMonth) {
                                  rowClass = "border-info border-start shadow-xs border-3";
                                  rowStyle = { backgroundColor: '#f0f8ff' }; // Bleu très clair pour le reste du mois
                                } else if (isAlreadyPassedThisMonth) {
                                  rowClass = "border-success border-start border-3";
                                  rowStyle = { backgroundColor: '#f8f9fa', opacity: 0.85 }; // 🚀 Gris très clair et léger fondu pour les passés
                                }

                                return (
                                  <li key={b.id} className={`d-flex justify-content-between align-items-center bg-white p-2 rounded-2 ${rowClass}`}
                                    style={rowStyle}
                                  >
                                    <div className="d-flex flex-column gap-1">
                                      {/* 🚀 Prénom + Âge fêté */}
                                      <div className="d-flex align-items-center gap-2 flex-wrap">
                                        <span className={`small ${isToday ? 'fw-semibold text-danger' : 'fw-semibold text-dark'}`}>
                                          {b.name}
                                        </span>

                                        {/* Affiche l'âge uniquement si le calcul est valide et supérieur à 0 */}
                                        {calculateAge(b.date) && (
                                          <span className="text-muted small" style={{ fontSize: '11px' }}>
                                            ({calculateAge(b.date)} ans)
                                          </span>
                                        )}

                                        {b.zodiacEmoji && (
                                          <span style={{ fontSize: '14px', cursor: 'help' }} title={`Signe astrologique : ${b.zodiacName}`}>
                                            {b.zodiacEmoji}
                                          </span>
                                        )}

                                        {isToday && (
                                          <React.Fragment>
                                            <span className="badge bg-danger text-white rounded-pill px-2 py-1 forced-blink" style={{ fontSize: '10px', fontWeight: 'bold' }}
                                            >
                                              🎉 Aujourd'hui !
                                            </span>
                                          </React.Fragment>
                                        )}

                                        {/* 🚀 L'ALERTE DYNAMIQUE : S'affiche uniquement si c'est le mois en cours */}
                                        {isThisMonth && (
                                          <span className="badge bg-info text-dark rounded-pill px-2 py-1" style={{ fontSize: '12px', opacity: 0.8 }}>
                                            📅 Ce mois-ci
                                          </span>
                                        )}

                                        {/* 🚀 NOUVEAU BADGE OPTIONNEL : S'affiche si l'anniversaire est passé ce mois-ci */}
                                        {isAlreadyPassedThisMonth && (
                                          <span className="badge bg-success text-white rounded-pill px-2 py-1" style={{ fontSize: '12px', opacity: 0.6 }}>
                                            ✅ Passé ce mois-ci
                                          </span>
                                        )}
                                      </div>
                                      <span className="text-muted" style={{ fontSize: '11px' }}>📅 {dateFormatee}</span>
                                    </div>

                                    <Button
                                      variant="link"
                                      className="text-danger p-0 border-0 ms-3 align-self-center"
                                      onClick={async (e) => {
                                        e.preventDefault(); // Bloque le comportement natif

                                        // 🚀 TEST DE SÉCURITÉ CONSOLE :
                                        console.log("Tentative de suppression de l'élément :", b);

                                        if (!b.id) {
                                          console.error("Impossible de supprimer : ID manquant");
                                          return;
                                        }

                                        try {
                                          // 🚀 SUPPRESSION DIRECTE DU DOCUMENT DANS FIRESTORE
                                          await db.collection('birthdays').doc(b.id).delete();
                                          triggerToast("Anniversaire supprimé avec succès !", 'success');
                                          console.log(`🧼 Anniversaire de ${b.name} supprimé de Firestore avec succès !`);
                                        } catch (err) {
                                          console.error("Erreur de suppression Firestore :", err);
                                        }
                                      }}
                                    >
                                      <i className="bi bi-trash3-fill"></i>
                                    </Button>
                                  </li>
                                );
                              })}
                            </ul>
                          )}
                      </div>
                    </div>
                  </XyzTransition>
                </div>

                <XyzTransition appear xyz='fade stagger delay-2 duration-10'>
                  <div className="w-md-auto d-flex flex-column align-items-center" style={{ maxWidth: '600px' }}>
                    <Card className="bg-dark border-secondary text-light shadow p-0 text-center">
                      <Card.Body>
                        {/* <p className="text-white-50">Gérez vos options et vos connexions externes.</p> */}
                        <p className="text-info fw-medium">🔌 Synchronisation externe</p>
                        {/* <hr className="border-secondary my-4" /> */}
                        {/* <h5 className="text-info fw-medium mb-1">🔌 Synchronisation externe</h5> */}
                        <GoogleSyncButton toastTrigger={triggerToast} />
                      </Card.Body>
                    </Card>
                  </div>
                </XyzTransition>


                {/* --- MODALE : AJOUTER UN ANNIVERSAIRE --- */}
                <Modal
                  show={showBirthdayModal}
                  onHide={() => {
                    setShowBirthdayModal(false);
                    setBirthdayForm({ name: '', date: '' });
                  }}
                  contentClassName="bg-dark text-light border-secondary"
                  backdrop="static"
                  centered
                >
                  <Modal.Header closeButton closeVariant="white">
                    <Modal.Title className="text-warning fw-semibold">Ajouter un anniversaire</Modal.Title>
                  </Modal.Header>
                  <Form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!birthdayForm.name || !birthdayForm.date) return;

                    const activeUid = firebase.auth().currentUser?.uid;
                    if (!activeUid) return;

                    // 🚀 Calcul automatique du signe
                    const zodiac = getZodiacSign(birthdayForm.date);

                    try {
                      // 🚀 ENVOI DIRECT À FIRESTORE
                      await db.collection('birthdays').add({
                        authorId: activeUid, // L'ID pour cloisonner les comptes
                        name: birthdayForm.name,
                        date: birthdayForm.date,
                        zodiacName: zodiac.name,
                        zodiacEmoji: zodiac.emoji,
                        createdAt: firebase.firestore.FieldValue.serverTimestamp() // Date d'enregistrement serveur
                      });

                      setShowBirthdayModal(false);
                      setBirthdayForm({ name: '', date: '' });
                      triggerToast("Anniversaire ajouté !", 'success');
                    } catch (err) {
                      triggerToast("Erreur lors de l'ajout", 'danger');
                      console.error("Erreur lors de l'ajout Firestore :", err);
                    }
                  }}>
                    <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', paddingBottom: '20px' }}>
                      <Form.Group className="mb-2" controlId="birthdayName">
                        <Form.Label className="text-light fw-semibold mb-1">Nom / Prénom de la personne</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="Ex: Jean Dupont"
                          value={birthdayForm.name}
                          onChange={(e) => setBirthdayForm({ ...birthdayForm, name: e.target.value })}
                          required
                        />
                      </Form.Group>

                      <Form.Group className="mb-2" controlId="birthdayDate">
                        <Form.Label className="text-light fw-semibold mb-1">Date de naissance complète</Form.Label>
                        <Form.Control
                          type="date"
                          value={birthdayForm.date}
                          onChange={(e) => setBirthdayForm({ ...birthdayForm, date: e.target.value })}
                          required
                        />
                        {/* 🚀 2. On ajoute un petit texte d'aide discret en dessous du champ */}
                        <Form.Text className="text-light" style={{ fontSize: '13px' }}>
                          💡 Renseignez bien l'année de naissance pour calculer l'âge automatiquement.
                          </Form.Text>
                      </Form.Group>
                    </Modal.Body>

                    <Modal.Footer className="d-flex justify-content-end mt-1 border-secondary border-1">
                      <Button variant="secondary" className="px-3 fw-semibold" onClick={() => setShowBirthdayModal(false)}>
                        Annuler
                        </Button>
                      <Button variant="warning" className="px-3 fw-semibold" type="submit">
                        Enregistrer
                        </Button>
                    </Modal.Footer>
                  </Form>
                </Modal>

                {/* --- MODALE : AJOUTER UN DÉCÈS --- */}
                <Modal show={showDeathModal} onHide={() => setShowDeathModal(false)} centered contentClassName="bg-dark text-light border-secondary" backdrop="static">
                  <Modal.Header closeButton closeVariant="white">
                    <Modal.Title className="text-warning fw-semibold">Commémorer un départ</Modal.Title>
                  </Modal.Header>
                  <Form onSubmit={async (e) => {
                    e.preventDefault();
                    if (!deathForm.name || !deathForm.deathDate) return;

                    const activeUid = firebase.auth().currentUser?.uid;
                    if (!activeUid) return;

                    try {
                      // 🚀 ENVOI DIRECT À LA COLLECTION FIRESTORE 'deaths'
                      await db.collection('deaths').add({
                        authorId: activeUid,
                        name: deathForm.name,
                        birthDate: deathForm.birthDate || '', // Optionnelle
                        deathDate: deathForm.deathDate,       // Obligatoire
                        createdAt: firebase.firestore.FieldValue.serverTimestamp()
                      });

                      setShowDeathModal(false);
                      setDeathForm({ name: '', birthDate: '', deathDate: '' });
                      triggerToast("Commémoration ajouté !", 'success');
                      console.log("🕊️ Commémoration synchronisée sur Firestore !");
                    } catch (err) {
                      triggerToast("Erreur lors de l'ajout", 'danger');
                      console.error("Erreur lors de l'ajout du décès Firestore :", err);
                    }
                  }}>
                    <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto', paddingBottom: '20px' }}>
                      <Form.Group className="mb-2" controlId="deathName">
                        <Form.Label className="text-light fw-semibold mb-1">Nom / Prénom de la personne</Form.Label>
                        <Form.Control
                          required
                          type="text"
                          placeholder="Ex: Jean Dupont"
                          value={deathForm.name}
                          onChange={(e) => setDeathForm({ ...deathForm, name: e.target.value })}
                        />
                      </Form.Group>

                      <Form.Group className="mb-2" controlId="deathBirthDate">
                        <Form.Label className="text-light fw-semibold mb-1">Date de naissance (Optionnel)</Form.Label>
                        <Form.Control
                          type="date"
                          value={deathForm.birthDate}
                          onChange={(e) => setDeathForm({ ...deathForm, birthDate: e.target.value })}
                        />
                        <Form.Text className="text-light small">
                          💡 Permet de calculer l'âge qu'avait la personne à son départ.
                          </Form.Text>
                      </Form.Group>

                      <Form.Group className="mb-2" controlId="deathDate">
                        <Form.Label className="text-light fw-semibold mb-1">Date du décès</Form.Label>
                        <Form.Control
                          required
                          type="date"
                          value={deathForm.deathDate}
                          onChange={(e) => setDeathForm({ ...deathForm, deathDate: e.target.value })}
                        />
                      </Form.Group>
                    </Modal.Body>

                    <Modal.Footer className="d-flex justify-content-end border-secondary border-1 mt-1">
                      <Button variant="secondary" className="px-3 fw-semibold" onClick={() => setShowDeathModal(false)}>
                        Annuler
                        </Button>
                      <Button variant="secondary" type="submit" className="px-3 fw-semibold" style={{ backgroundColor: '#4a5568' }}>
                        Enregistrer
                        </Button>
                    </Modal.Footer>
                  </Form>
                </Modal>
              </Container>


              <ToastContainer position="bottom-end" className="py-5" style={{ position: 'fixed', top: '15px', zIndex: 9999 }}>
                <Toast
                  onClose={() => setShowToast(false)}
                  show={showToast}
                  delay={3000}
                  autohide
                  bg={toastVariant}
                  className="text-white shadow border-0"
                >
                  <div className="d-flex align-items-center p-2">
                    <Toast.Body className="d-flex justify-content-between align-items-center py-2 px-3">
                      <i className="material-icons me-2" style={{ verticalAlign: 'middle' }}>
                        {toastVariant === 'success' ? 'check_circle' : 'error'}
                      </i>
                      <span className="fw-medium">{toastMessage}</span>
                    </Toast.Body>
                    <Button
                      variant="close"
                      variant-white="true"
                      className="me-2 m-auto btn-close-white"
                      onClick={() => setShowToast(false)}
                      aria-label="Fermer"
                    />
                  </div>
                </Toast>
              </ToastContainer>
            </Container>
          </div>
        </div>
      </Container>
    </React.Fragment>
  );
}

export default CalendarPage;

